import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { AlertTriangle, CalendarClock, Eye, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader, LoadingState, MetricCard, KPIGrid } from '@/components/common';
import { Card, CardContent, CardHeader, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { permitsApi, type Watchlist, type WatchlistRow } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { cn, formatDate } from '@/lib/utils';

/**
 * Bands, not a flat list sorted by date. The question a user brings here is
 * "what needs me now", and a deadline in six days and one in six months are
 * different kinds of problem, not the same problem at different offsets.
 */
const BANDS = [
  {
    key: 'breached' as const,
    title: 'Breached',
    blurb: 'Past the re-export deadline. The security lodged against these permits is at risk.',
    tone: 'destructive',
    icon: AlertTriangle,
  },
  {
    key: 'actNow' as const,
    title: 'Act now',
    blurb: 'Due within 14 days. Renew the permit or raise the outbound movement.',
    tone: 'warning',
    icon: CalendarClock,
  },
  {
    key: 'planNow' as const,
    title: 'Plan now',
    blurb: 'Due within 45 days. Enough room to complete a renewal if one is needed.',
    tone: 'info',
    icon: Eye,
  },
  {
    key: 'monitor' as const,
    title: 'Monitor',
    blurb: 'Beyond 45 days. Nothing to do yet.',
    tone: 'neutral',
    icon: ShieldCheck,
  },
];

const toneClasses: Record<string, { border: string; bg: string; text: string }> = {
  destructive: {
    border: 'border-status-destructive-border',
    bg: 'bg-status-destructive-subtle',
    text: 'text-status-destructive-foreground',
  },
  warning: {
    border: 'border-status-warning-border',
    bg: 'bg-status-warning-subtle',
    text: 'text-status-warning-foreground',
  },
  info: {
    border: 'border-status-info-border',
    bg: 'bg-status-info-subtle',
    text: 'text-status-info-foreground',
  },
  neutral: {
    border: 'border-border',
    bg: 'bg-muted',
    text: 'text-muted-foreground',
  },
};

export default function PermitWatchlist() {
  const navigate = useNavigate();
  const [data, setData] = useState<Watchlist | null>(null);
  const [loading, setLoading] = useState(true);
  const [discharging, setDischarging] = useState<WatchlistRow | null>(null);
  const [proof, setProof] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setData(await permitsApi.getWatchlist());
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const discharge = async () => {
    if (!discharging) return;
    try {
      setSaving(true);
      await permitsApi.dischargeObligation(discharging.id, proof);
      toast.success('Obligation discharged');
      setDischarging(null);
      setProof('');
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) return <LoadingState />;

  const total =
    data.breached.length + data.actNow.length + data.planNow.length + data.monitor.length;

  return (
    <motion.div variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Expiry Watchlist"
          description="Assets held abroad under a temporary regime, with the deadline for returning each one."
        />
      </motion.div>

      <div className="mt-6">
        <KPIGrid columns={4}>
          <MetricCard
            label="Breached"
            value={data.breached.length}
            sentiment={data.breached.length > 0 ? 'danger' : 'success'}
            subtitle="security at risk"
            icon={<AlertTriangle className="h-5 w-5" />}
          />
          <MetricCard
            label="Act Now"
            value={data.actNow.length}
            sentiment={data.actNow.length > 0 ? 'warning' : 'success'}
            subtitle="within 14 days"
            icon={<CalendarClock className="h-5 w-5" />}
          />
          <MetricCard
            label="Plan Now"
            value={data.planNow.length}
            sentiment="info"
            subtitle="within 45 days"
            icon={<Eye className="h-5 w-5" />}
          />
          <MetricCard
            label="Assets in Country"
            value={total}
            sentiment="neutral"
            subtitle="under temporary regime"
            icon={<ShieldCheck className="h-5 w-5" />}
          />
        </KPIGrid>
      </div>

      {total === 0 && (
        <motion.div variants={fadeUp} className="mt-6">
          <Card>
            <CardContent className="py-12 text-center">
              <ShieldCheck className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
              <p className="font-medium text-foreground">No open obligations</p>
              <p className="text-sm text-muted-foreground">
                Nothing is currently in country under a temporary regime.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {BANDS.map((band) => {
        const rows = data[band.key];
        if (rows.length === 0) return null;
        const tone = toneClasses[band.tone];
        const Icon = band.icon;

        return (
          <motion.div key={band.key} variants={fadeUp} className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Icon className={cn('h-4 w-4', tone.text)} />
                  <h3 className="text-base font-semibold text-foreground">
                    {band.title}
                    <span className="ml-2 text-sm font-normal text-muted-foreground">
                      {rows.length}
                    </span>
                  </h3>
                </div>
                <CardDescription>{band.blurb}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {rows.map((row) => (
                    <div
                      key={row.id}
                      className={cn(
                        'flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3',
                        tone.border,
                        tone.bg
                      )}
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-foreground">{row.description}</p>
                        <p className="text-xs text-muted-foreground">
                          {row.assetReference ? `${row.assetReference} · ` : ''}
                          {row.caseNumber}
                          {row.permitReference ? ` · ${row.permitReference}` : ''}
                          {row.country ? ` · ${row.country}` : ''}
                        </p>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className={cn('text-sm font-semibold tabular-nums', tone.text)}>
                            {row.daysRemaining < 0
                              ? `${Math.abs(row.daysRemaining)}d overdue`
                              : `${row.daysRemaining}d left`}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            due {formatDate(row.deadline)}
                            {row.cumulativeDays > 0 && ` · ${row.cumulativeDays}d in country`}
                          </p>
                        </div>
                        <div className="flex gap-1.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/permits/${row.caseId}`)}
                          >
                            Case
                          </Button>
                          <Button size="sm" onClick={() => setDischarging(row)}>
                            Discharge
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );
      })}

      <Dialog open={!!discharging} onOpenChange={(o) => !o && setDischarging(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discharge obligation</DialogTitle>
            <DialogDescription>
              Confirms {discharging?.description} has left the country. Proof of exit is required —
              the authority wants that document before releasing the guarantee, so closing without
              it would show the case as clean while the security is still held.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="proof">Proof of exit reference</Label>
            <Input
              id="proof"
              value={proof}
              onChange={(e) => setProof(e.target.value)}
              placeholder="Customs discharge certificate or exit document number"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDischarging(null)}>
              Cancel
            </Button>
            <Button onClick={discharge} disabled={saving || !proof.trim()}>
              Discharge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
